import React, { useState } from 'react';
import _get from 'lodash/get';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { transparentize } from 'polished';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import Divider from '@material-ui/core/Divider';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import CakeIcon from '@material-ui/icons/Cake';
import TodayIcon from '@material-ui/icons/Today';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';

import CreateIcon from '@material-ui/icons/Create';
import { PersonListsByDatePersonList } from 'pages/Homepage/typings';

interface Props {
  list: PersonListsByDatePersonList;
  listIndex: number;
  disableDelete: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  onDeleteList: (index: number) => void;
}

function PersonsListByDatePresetSelect({
  list,
  listIndex,
  disableDelete,
  setFieldValue,
  onDeleteList,
}: Props) {
  const classes = useStyles();

  const PRESETS: Array<{
    name: string;
    description: string;
    icon: React.ReactNode;
    defaultSettings: {
      name: string;
      displayYear: boolean;
      includeYearly: boolean;
      fieldObjectKey?: string;
      updateFrequency: string;
      includeInFirstYear: boolean;
    };
  }> = [
    {
      name: 'Birthdays',
      description: 'Highlight recent and upcoming birthdays.',
      icon: <CakeIcon className={classes.icon} />,
      defaultSettings: {
        name: 'Birthdays',
        displayYear: false,
        includeYearly: true,
        fieldObjectKey: undefined,
        updateFrequency: 'weekly',
        includeInFirstYear: true,
      },
    },
    {
      name: 'Work Anniversaries',
      description:
        'Celebrate team members who recently had a work anniversary by showcasing their years of service.',
      icon: <TodayIcon className={classes.icon} />,
      defaultSettings: {
        name: 'Anniversaries',
        displayYear: true,
        includeYearly: true,
        fieldObjectKey: 'anniversaryDate',
        updateFrequency: 'weekly',
        includeInFirstYear: false,
      },
    },
    {
      name: 'New Hires',
      description: 'Welcome new hires to your organization and help others get to know them.',
      icon: <AssignmentIndIcon className={classes.icon} />,
      defaultSettings: {
        name: 'New Hires',
        displayYear: false,
        includeYearly: false,
        fieldObjectKey: 'anniversaryDate',
        updateFrequency: 'weekly',
        includeInFirstYear: true,
      },
    },
    {
      name: 'Custom',
      description:
        'Create a customized list to showcase any date-based milestone that matters most to your organization.',
      icon: <CreateIcon className={classes.icon} />,
      defaultSettings: {
        name: 'Custom List',
        displayYear: true,
        includeYearly: true,
        fieldObjectKey: undefined,
        updateFrequency: 'weekly',
        includeInFirstYear: true,
      },
    },
  ];

  const listName = _get(list, 'name', '');

  const [selectedPresetIndex, setSelectedPresetIndex] = useState(-1);
  const handleSelect = (value: any) => {
    setSelectedPresetIndex(value);
  };

  const handleChoosePreset = () => {
    const selectedPreset = PRESETS[selectedPresetIndex];

    setFieldValue(`settings.personLists[${listIndex}]`, {
      ...selectedPreset.defaultSettings,
      _onPresetSelectStep: false,
      _targetListIndex: list._targetListIndex,
    });
  };

  return (
    <Card className={classes.root}>
      <div className={classes.section}>
        <div>
          <Typography variant="h6">
            List #{listIndex + 1}
            {listName ? ':' : ''} {listName}
          </Typography>
        </div>
        <Tooltip
          title={disableDelete ? 'You must have at least one list' : ''}
          placement="top"
          arrow
        >
          <div>
            <Button
              onClick={() => onDeleteList(listIndex)}
              className={classes.deleteButton}
              disabled={disableDelete}
            >
              Delete List
            </Button>
          </div>
        </Tooltip>
      </div>

      <Divider variant="fullWidth" className={classes.divider} />

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div className={classes.section}>
          <div>
            <Typography variant="h6">Choose a Preset</Typography>
            <Typography variant="subtitle2">
              Choose a preset below to get started with configuring your list.
            </Typography>
          </div>
        </div>

        <RadioGroup value={selectedPresetIndex}>
          <div className={classes.cardContainer}>
            {PRESETS.map((preset, index: number) => {
              const { icon, name, description } = preset;

              return (
                <Card onClick={() => handleSelect(index)} className={classes.presetCard}>
                  <div className={classes.checkDiv}>
                    <Radio value={index} />
                  </div>
                  <div className={classes.cardContent}>
                    <div>{icon}</div>
                    <Typography variant="body1" className={classes.presetName}>
                      {name}
                    </Typography>
                    <Typography variant="body2" className={classes.presetDescription}>
                      {description}
                    </Typography>
                  </div>
                </Card>
              );
            })}
          </div>
        </RadioGroup>

        <Button
          onClick={handleChoosePreset}
          color="secondary"
          variant="contained"
          className={classes.button}
        >
          Choose Preset
        </Button>
      </div>
    </Card>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    paddingTop: theme.spacing(3),
  },
  icon: {
    height: 80,
    width: 80,
    color: transparentize(0.46, theme.palette.common.black),
  },
  divider: {
    margin: 0,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
  },
  section: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  cardContainer: {
    display: 'flex',
    height: '100%',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    marginTop: theme.spacing(2.75),
    marginBottom: theme.spacing(10),
  },
  presetCard: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 250,
    minHeight: 321,
    width: '100%',
    marginRight: theme.spacing(),
    padding: theme.spacing(2),
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  presetName: {
    fontWeight: theme.typography.fontWeightSemiBold,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1.5),
  },
  presetDescription: {
    maxWidth: 175,
    textAlign: 'center',
  },
  checkDiv: {
    marginLeft: 'auto',
    alignSelf: 'flex-end',
    height: 42,
    marginBottom: theme.spacing(3),
  },
  button: {
    marginLeft: 'auto',
    marginRight: theme.spacing(3),
    marginBottom: theme.spacing(4.5),
    maxHeight: 36,
  },
  deleteButton: {
    color: theme.colors.error,
  },
}));

export default PersonsListByDatePresetSelect;
